import React, { forwardRef } from 'react'
import Icon, { IconName, IconSize, IndicatorColor } from '../Icon'
import styled, { CSSObject } from '../themes/styled'
import { LoaderSize } from '../Loader/SectionLoader'
import {
    ButtonProps,
    withLinkWrapper,
    ButtonContainer as BaseButtonContainer,
    Loader,
    ButtonForm as BaseButtonForm,
} from '.'

export type ButtonSize = 'l' | 'm' | 's' | 'xs'
type ButtonForm = Exclude<BaseButtonForm, 'text'>

interface ButtonContainerProps extends ButtonProps {
    size: ButtonSize
}

const sizeMap: Record<ButtonSize, { iconSize: IconSize; loaderSize: LoaderSize; style: CSSObject }> = {
    l: {
        iconSize: '24px',
        loaderSize: 'l',
        style: { width: '40px', height: '40px' },
    },
    m: {
        iconSize: '20px',
        loaderSize: 'm',
        style: { width: '32px', height: '32px' },
    },
    s: {
        iconSize: '20px',
        loaderSize: 'm',
        style: {
            width: '28px',
            height: '28px',
        },
    },
    xs: {
        iconSize: '16px',
        loaderSize: 's',
        style: {
            width: '20px',
            height: '20px',
        },
    },
}

const ButtonContainer = styled(BaseButtonContainer)<ButtonContainerProps>`
    display: flex;
    flex-shrink: 0;
    padding: 0;
    ${({ size }) => sizeMap[size].style}
`

export interface IconButtonProps extends ButtonProps {
    size?: ButtonSize
    form?: ButtonForm
    icon: IconName
    indicatorColor?: IndicatorColor
    isActivated?: boolean
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            size = 'm',
            color = 'primary',
            form = 'filled',
            isDisabled = false,
            isLoading = false,
            icon,
            iconForm,
            indicatorColor,
            isActivated,
            className,
            ...props
        },
        ref,
    ) => {
        const { iconSize, loaderSize } = sizeMap[size]
        return (
            <ButtonContainer
                ref={ref}
                size={size}
                color={color}
                form={form}
                isDisabled={isDisabled}
                isLoading={isLoading}
                className={`${className} ${isActivated ? 'isActivated' : ''}`}
                {...props}
            >
                {isLoading ? (
                    <Loader size={loaderSize} spinnerColor="currentColor" />
                ) : (
                    icon && (
                        <Icon
                            form={iconForm}
                            className="button-icon"
                            name={icon}
                            size={iconSize}
                            color="currentColor"
                            indicatorColor={indicatorColor}
                        />
                    )
                )}
            </ButtonContainer>
        )
    },
)

IconButton.displayName = 'IconButton'

export default IconButton
export const IconLinkButton = withLinkWrapper(IconButton)
